import React, { forwardRef, useCallback, useRef, useState, useImperativeHandle } from "react";
import { RCCGrid, RCCHookToast, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import List from "src/components/List";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import OverflowSpan from "src/components/OrgChart/components/ListKR/styles";
import Title from "src/components/Title";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import formatDate from "src/utils/formatDate";
import formatNumber from "src/utils/formatNumber";
import {IColumn} from "src/components/List/interface";
import {formatMinutesInHours} from "src/utils/formartHourMinutes";
import { IPhases } from "../Project/Components/Phases/interface";
import { IProject } from "../Project/interface";

export interface IProjectDetailsRef{
    openModalDetails: (id: number, type: "project" | "phase", title: string, controlHours: boolean, onCallback?: Function) => void;
}

export interface IProjectDetails{
	name: string;
	dateStart: Date;
	dateEnd: Date;
	expense: number;
	revenue: number;
	hour?: number;
}

const ModalDetails: React.ForwardRefRenderFunction<IProjectDetailsRef> = (_, ref) => {
	const { translate, currentLanguage } = useTranslation();
	const { setLoading } = useLoaderBackdrop();
	const { addToast } = RCCHookToast();
    
	// Modal details
	const modalDetail = useRef<RCCRefModalProps>(null);
	const [subtitle, setSubtitle] = useState<string | undefined>(undefined);
	const [typeDetail, setTypeDetail] = useState<"project" | "phase">();
	const [details, setDetails] = useState<IProjectDetails[]>();
	const [project, setProject] = useState<IProject>();
	const [controlHours, setControlHours] = useState(false);

	const openModalDetails = useCallback(async (id: number, type: "project" | "phase", title: string, controlHoursX: boolean) => {
		try{
			setControlHours(controlHoursX);
			setLoading(true);

			const {data} = await RCCServicesApi.getRequest({
				method: type === "project" ? "Project/Select" : "ProjectPhase/Select",
				urlParameters: {id}
			});

			let hoursController = {} as any;
			if(controlHoursX){
				hoursController = await RCCServicesApi.getRequest({
					method: type === "project" ? "Project/GetHours" : "ProjectPhase/GetHours",
					urlParameters: {id}
				});
			}

			if(type === "project"){
				const projeto = data as IProject;
				setProject(projeto);
	
				setDetails([{
					name: translate("Estimado"),
					dateStart: projeto.pra002Dtiniest,
					dateEnd: projeto.pra002Dtfimest,
					expense: projeto.pra002Vlrestimado,
					revenue: projeto.pra002Vlrestreceita,
					hour: controlHoursX ? hoursController.data.estimated : undefined
				},{
					name: translate("Previsto (Ações)"),
					dateStart: projeto.pra002Dtiniprev,
					dateEnd: projeto.pra002Dtfimprev,
					expense: projeto.pra002Vlrprevisto,
					revenue: projeto.pra002Vlrprevistoreceita,
					hour: controlHoursX ? hoursController.data.foreseen : undefined
				},{
					name: translate("Real (Ações)"),
					dateStart: projeto.pra002Dtinireal,
					dateEnd: projeto.pra002Dtfimreal,
					expense: projeto.pra002Vlrrealizado,
					revenue: projeto.pra002Vlrrealizadoreceita,
					hour: controlHoursX ? hoursController.data.real : undefined
				}]);
			}else{
				const phase = data as IPhases;
	
				setDetails([{
					name: translate("Estimado"),
					dateStart: phase.pra003Dtiniest,
					dateEnd: phase.pra003Dtfimest,
					expense: phase.pra003Vlrestimado,
					revenue: phase.pra003Vlrestreceita,
					hour: controlHoursX ? hoursController.data.estimated : undefined
				},{
					name: translate("Previsto (Ações)"),
					dateStart: phase.pra003Dtiniprev,
					dateEnd: phase.pra003Dtfimprev,
					expense: phase.pra003Vlrprevisto,
					revenue: phase.pra003Vlrprevistoreceita,
					hour: controlHoursX ? hoursController.data.foreseen : undefined
				},{
					name: translate("Real (Ações)"),
					dateStart: phase.pra003Dtinireal,
					dateEnd: phase.pra003Dtfimreal,
					expense: phase.pra003Vlrrealizado,
					revenue: phase.pra003Vlrrealizadoreceita,
					hour: controlHoursX ? hoursController.data.real : undefined
				}]);
			}

			setLoading(false);
			setTypeDetail(type);
			setSubtitle(title);
			modalDetail.current?.openModal();
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar detalhes"), addToast);
		}
	}, []);

	useImperativeHandle(ref, () => ({
		openModalDetails
	}));

	const colName = (row: any) => (
		<>
			<OverflowSpan>
				{row.name}
			</OverflowSpan>
		</>
	);

	const colDateStart = (row: any) => (
		<>
			<span>
				{formatDate(row.dateStart, currentLanguage)}
			</span>
		</>
	);

	const colDateEnd = (row: any) => (
		<>
			<span>
				{formatDate(row.dateEnd, currentLanguage)}
			</span>
		</>
	);

	const colExpense = (row: any) => (
		<>
			<span>
				{formatNumber(row.expense, currentLanguage, 2)}
			</span>
		</>
	);

	const colRevenue = (row: any) => (
		<>
			<span>
				{formatNumber(row.revenue, currentLanguage, 2)}
			</span>
		</>
	);

	const colHours = (row: any) => (
		<>
			<span>
				{formatMinutesInHours(row.hour)}
			</span>
		</>
	);

	const listColumns = (): Array<IColumn> => {

		const colunas = [{
			idColumn: "name",
			nameColumn: translate(""),
			xs: 12,
			sm: 4,
			justify: "left",
			truncate: true,
			cellRenderer: colName
		},{
			idColumn: "dateStart",
			nameColumn: translate("Data Inicial"),
			xs: 12,
			sm: 2,
			justify: "left",
			type: "date",
			cellRenderer: colDateStart
		},
		{
			idColumn: "dateEnd",
			nameColumn: translate("Data Final"),
			xs: 12,
			sm: 2,
			justify: "left",
			type: "date",
			cellRenderer: colDateEnd
		},
		{
			idColumn: "expense",
			nameColumn: translate("Valor Despesa"),
			justify: "left",
			xs: 12,
			sm: 2,
			cellRenderer: colExpense
		},
		{
			idColumn: "revenue",
			nameColumn: translate("Valor Receita"),
			justify: "left",
			xs: 12,
			sm: 2,
			cellRenderer: colRevenue
		}] as Array<IColumn>;

		if(controlHours){
			const columnHour = {
				idColumn: "hours",
				nameColumn: translate("Horas"),
				justify: "left",
				xs: 12,
				sm: 2,
				cellRenderer: colHours
			} as IColumn;

			colunas.push(columnHour);
		}

		return colunas;
	};

	return (
		<>
			<Modal
				ref={modalDetail}
				header={{
					title: translate("Detalhes"),
					icon: {
						name: "info"
					}
				}}
				footer= {{
					showSaveButton: false,
					cancelButtonText: translate("Fechar")
				}}
				styleInline={ModalSide}
			>
				{
					typeDetail === "project" &&
					<>
						{project?.fullTitle &&
							<RCCGrid>
								<Title styleInline="margin-left: 0"
									color="purple"
								>
									{translate("Projeto")}
								</Title>
								<small><strong>{project?.fullTitle}</strong></small>
								<hr/>
								<br/>
							</RCCGrid>
						}
						{project?.pra002Descricao &&
							<RCCGrid>
								<Title styleInline="margin-left: 0"
									color="purple"
								>
									{translate("Descrição")}
								</Title>
								<small>{project?.pra002Descricao}</small>
								<br/><br/>
							</RCCGrid>
						}
						{project?.pra002Objetivo &&
							<RCCGrid>
								<Title styleInline="margin-left: 0"
									color="purple"
								>
									{translate("Objetivo")}
								</Title>
								<small>{project?.pra002Objetivo}</small>
								<br/><br/>
							</RCCGrid>
						}
						{project?.pra002Justificativa &&
							<RCCGrid>
								<Title styleInline="margin-left: 0"
									color="purple"
								>
									{translate("Justificativa")}
								</Title>
								<small>{project?.pra002Justificativa}</small>
								<br/><br/>
							</RCCGrid>
						}
						{project?.pra002Taa001Nome &&
							<RCCGrid>
								<Title styleInline="margin-left: 0"
									color="purple"
								>
									{translate("Setor")}
								</Title>
								<small>{project?.pra002Taa001Nome}</small>
							</RCCGrid>
						}
					</>
				}
				{
					typeDetail === "phase" && 
					<RCCGrid>
						<Title styleInline="margin-left: 0"
							color="purple"
						>
							{translate("Fase")}
						</Title>
						<small><strong>{subtitle}</strong></small>
						<hr/>
					</RCCGrid>
				}


				<RCCGrid
					container
					style={{ marginTop: "20px" }}
				>
					<List 
						showHeader
						data={details || []}
						columns={listColumns()}
						layout="table"
					/>
				</RCCGrid>
				
				{project?.prtba008 && project?.prtba008.length > 0 &&
					<RCCGrid
						container
						style={{ marginTop: "20px" }}
					>
						<List 
							showHeader
							data={project?.prtba008 || []}
							columns={[{
								idColumn: "pra008Premissa",
								nameColumn: translate("Premissas"),
								xs: 12,
								truncate: false,
								justify: "left"
							}]}
							layout="table"
						/>
					</RCCGrid>
				}
				{project?.prtba009 && project?.prtba009.length > 0 &&
					<RCCGrid
						container
						style={{ marginTop: "20px" }}
					>
						<List 
							showHeader
							data={project?.prtba009 || []}
							columns={[{
								idColumn: "pra009Restricao",
								nameColumn: translate("Restrições"),
								xs: 12,
								truncate: false,
								justify: "left"
							}]}
							layout="table"
						/>
					</RCCGrid>
				}
			</Modal>
		</>
	);
};

export default forwardRef(ModalDetails);
